import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import { DataGrid, GridColDef, ptBR } from '@mui/x-data-grid';
import { userService } from '../../services/userService';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import CustomPagination from '../../components/CustomPagination';
import DeleteIcon from '@mui/icons-material/Delete';

interface User {
  id: number;
  name: string;
  created_date: string;
  has_biometry: boolean;
}

const UserList = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [totalRows, setTotalRows] = useState(0);
  const navigate = useNavigate();
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deletingName, setDeletingName] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState(false);

  const columns: GridColDef[] = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 90 
    },
    { 
      field: 'name', 
      headerName: 'Nome', 
      flex: 1,
      minWidth: 200,
    },
    { 
      field: 'has_biometry', 
      headerName: 'Biometria', 
      width: 120,
      renderCell: (params) => (
        <Chip 
          label={params.value ? 'Sim' : 'Não'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    { 
      field: 'created_date', 
      headerName: 'Data de Cadastro', 
      flex: 1,
      minWidth: 200,
      valueFormatter: (params) => {
        return format(new Date(params.value), 'dd/MM/yyyy HH:mm');
      },
    },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Ver detalhes">
            <IconButton
              onClick={() => navigate(`/users/${params.row.id}`)}
              size="small"
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Deletar usuário">
            <IconButton
              onClick={() => handleDeleteClick(params.row.id, params.row.name)}
              size="small"
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await userService.listUsers(
        paginationModel.page + 1,
        paginationModel.pageSize
      );
      
      setUsers(response.content);
      setTotalRows(response.total);
    } catch (err) {
      setError('Erro ao carregar usuários. Tente novamente mais tarde.');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize]);

  const handleDeleteClick = (id: number, name: string) => {
    setDeletingId(id);
    setDeletingName(name);
  };

  const handleDeleteConfirm = async () => {
    if (deletingId === null) return;

    try {
      setIsDeleting(true);
      await userService.deleteUser(deletingId);
      fetchUsers(); // Recarrega a lista
    } catch (err) {
      setError('Erro ao deletar usuário. Tente novamente mais tarde.');
      console.error('Error deleting user:', err);
    } finally {
      setIsDeleting(false);
      setDeletingId(null);
      setDeletingName('');
    }
  };

  const handleDeleteCancel = () => {
    setDeletingId(null);
    setDeletingName('');
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3
      }}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Usuários
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/users/create')}
        >
          Criar Usuário
        </Button>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={users}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            rowCount={totalRows}
            loading={loading}
            paginationMode="server"
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            disableRowSelectionOnClick
            slots={{
              pagination: CustomPagination,
            }}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </Box>
      </Paper>

      <Dialog
        open={deletingId !== null}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          Tem certeza que deseja excluir o usuário "{deletingName}"?
          Esta ação não pode ser desfeita.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : null}
          >
            {isDeleting ? 'Deletando...' : 'Deletar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserList; 